import {
  Text,
  View
} from "@react-pdf/renderer";


const cssToReactPdfName = (cssProp) => {
  return cssProp.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
};


const parseInlineStyle = (styleString) => {
  if (!styleString) return {};

  const styleObject = {};
  const rules = styleString.split(';');

  rules.forEach(rule => {
    const parts = rule.split(':').map(p => p.trim());
    if (parts.length === 2) {
      let [property, value] = parts;
      const reactPdfProperty = cssToReactPdfName(property);
      let parsedValue = parseValue(property, value);
      if (reactPdfProperty.includes('Weight') && typeof parsedValue === 'string') {
        parsedValue = parsedValue.toLowerCase() === 'bold' ? 'bold' : parsedValue;
      }
      if (reactPdfProperty.includes('justify') || reactPdfProperty.includes('align')) {
        styleObject.display = 'flex';
      }
      styleObject[reactPdfProperty] = parsedValue;
    }
  });

  return styleObject;
};

const parseValue = (property, value) => {
  value = value.trim();
  if (value.endsWith('%') || value === 'auto' || value === 'inherit') {
    return value;
  }

  const numericValue = parseFloat(value.replace(/px/g, ''));
  if (isNaN(numericValue)) return value;

  if (property.includes('font') && !property.includes('family')) {
    return numericValue * 0.75;
  }

  return numericValue;
};


export const renderHtmlToReactPdf = (node, inheritedTextStyle) => {
  if (node.nodeType === Node.TEXT_NODE) {
    let textContent = node.textContent;
    textContent = textContent.replace(/^\s+|\s+$/g, '');
    if (textContent === '') return null;
    textContent = textContent.replace(/\s+/g, ' ');
    return <Text style={inheritedTextStyle}>{textContent}</Text>;
  }

  const inlineStyles = parseInlineStyle(node.getAttribute('style'));

  const currentTextStyle = { ...inheritedTextStyle, ...inlineStyles };

  const children = Array.from(node.childNodes)
    .map((child, i) => renderHtmlToReactPdf(child, currentTextStyle))
    .filter(Boolean);

  const isBlockElement = ['DIV', 'P', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'IMG', 'BR', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD'].includes(node.nodeName);

  if (isBlockElement) {

    const blockStyles = {};
    const viewTextStyles = {};

    Object.keys(inlineStyles).forEach(key => {
      if (key.startsWith('margin') || key.startsWith('padding') || key.includes('Width') || key.includes('Height') || key.includes('flex') || key.includes('display') || key.includes('justify') || key.includes('align') || key.includes('background') || key.includes('border') || key.includes('position')) {
        blockStyles[key] = inlineStyles[key];
      } else {
        viewTextStyles[key] = inlineStyles[key];
      }
    });
    viewTextStyles.color = '#2B3034'
    viewTextStyles['font-weight'] = 100
    blockStyles['font-weight'] = 100
    blockStyles.color = '#2B3034'

    const finalTextStyle = { ...inheritedTextStyle, ...viewTextStyles };

    switch (node.nodeName) {
      case "P":
        if (blockStyles.marginBottom === undefined) blockStyles.marginBottom = 4;
        return (
          <View style={blockStyles}>
            <Text style={finalTextStyle}>
              {children}
            </Text>
          </View>
        );

      case "DIV":
        return (
          <View style={blockStyles}>
            {children}
          </View>
        );

      case "BR":
        return <Text style={{ fontSize: finalTextStyle.fontSize || 10, lineHeight: 1 }}>{"\n"}</Text>;

      case "H1":
      case "H2":
      case "H3":
      case "H4":
      case "H5":
      case "H6":
        const sizeMap = { "H1": 24, "H2": 20, "H3": 16, "H4": 14, "H5": 12, "H6": 10 };

        if (blockStyles.marginTop === undefined) blockStyles.marginTop = 12;
        if (blockStyles.marginBottom === undefined) blockStyles.marginBottom = 15;

        const headingTextStyle = {
          ...finalTextStyle,
          fontSize: finalTextStyle.fontSize || sizeMap[node.nodeName],
          fontWeight: 'bold',
        };
        return (
          <View style={blockStyles}>
            <Text style={headingTextStyle}>
              {children}
            </Text>
          </View>
        );

      case "UL":
      case "OL":
        if (blockStyles.marginBottom === undefined) blockStyles.marginBottom = 5;
        if (blockStyles.marginTop === undefined) blockStyles.marginTop = 5;

        return (
          <View style={blockStyles}>
            {children}
          </View>
        );

      case "LI":
        const bullet = node.parentNode.nodeName === "UL" ? '• ' : `${Array.from(node.parentNode.children).indexOf(node) + 1}. `;
        if (blockStyles.marginBottom === undefined) blockStyles.marginBottom = 3;
        return (
          <View style={{ ...blockStyles, flexDirection: 'row' }}>
            <Text style={{ ...finalTextStyle, width: 15, lineHeight: finalTextStyle.lineHeight || 1.1 }}>{bullet}</Text>
            <Text style={{ ...finalTextStyle, flex: 1, lineHeight: finalTextStyle.lineHeight || 1.1 }}>{children}</Text>
          </View>
        );

      case "TABLE":
      case "TBODY":
      case "THEAD":
        return (
          <View style={{ ...blockStyles, width: '100%' }}>
            {children}
          </View>
        );

      case "TR":
        return (
          <View style={{ ...blockStyles, flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#cccccc' }}>
            {children}
          </View>
        );

      case "TH":
        return (
          <View style={{ ...blockStyles, flex: 1, padding: 4, backgroundColor: '#f0f0f0' }}>
            <Text style={{ ...finalTextStyle, fontWeight: 'bold' }}>{children}</Text>
          </View>
        );

      case "TD":
        return (
          <View style={{ ...blockStyles, flex: 1, padding: 4 }}>
            <Text style={finalTextStyle}>{children}</Text>
          </View>
        );

      case "IMG":
        return null;

      default:
        return children.length > 0 ? <View style={blockStyles}>{children}</View> : null;
    }
  } else {
    let inlineOverrideStyle = {};
    if (node.nodeName === "B" || node.nodeName === "STRONG") {
      inlineOverrideStyle = {
        fontWeight: 'bold',
        color: '#000000',
      };
    } else if (node.nodeName === "I" || node.nodeName === "EM") {
      inlineOverrideStyle = {
        fontStyle: 'italic',
      };
    }
    return <Text style={{ ...currentTextStyle, ...inlineOverrideStyle }}>{children}</Text>;
  }
};